import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { ROUTE } from '@/routes/constants'
import { getSalt, login } from '@/services/generatedAPIs'
import { useAuth } from '@/contexts/AuthContext'
import { UserInfoResponseRole } from '@/services/generatedAPIs.schemas'
import { generateRequestSignature, hashPassword } from '@/lib/crypto'
import { unlockGamificationAudio } from '@/components/gamification/gamificationAudio'

// 登录页表单逻辑（接口调用、token 校验、表单校验状态）——抽成单一 Hook，
// 由 PC 端 <LoginForm> 与移动端 <MobileLoginForm> 共用，保证两套布局行为完全一致。
// 接口/请求逻辑沿用 dev 分支最新实现：登录错误码分流（80001009 凭据错误、
// 80007000 访问受限跳转），并在登录动作时解锁游戏化音频。
export function useLogin() {
  const { t } = useTranslation();
  const [loginMessage, setLogginMessage] = useState<string>("");
  const { tokenLogin, setCurrentUser } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isEmailInvalid, setEmailInvalid] = useState(false);
  const [isPswInvalid, setPswInvalid] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      tokenLogin().then((user) => {
        if (user) {
          if (user.role === UserInfoResponseRole.STUDENT) {
            navigate(ROUTE.STUDENT.OVERVIEW, { replace: true });
          } else if (user.role === UserInfoResponseRole.TEACHER) {
            navigate(ROUTE.TEACHER.OVERVIEW, { replace: true });
          }
        } else {
          localStorage.removeItem('token');
        }
      });
    }
  })

  const onLogin = async (formData: FormData) => {
    void unlockGamificationAudio();
    const email = formData.get("email") as string;
    const password = formData.get("password") as string;
    setIsLoading(true);
    try {
      const getSaltRsp = await getSalt({ email: email });
      if (getSaltRsp.code === 200 && getSaltRsp.data) {
        const loginRsp = await login({ email: email, password: hashPassword(password + getSaltRsp.data) }, {
          headers: {
            'X-Request-Signature': generateRequestSignature()
          }
        });
        if (loginRsp.code === 200) {
          setCurrentUser(loginRsp.data!.user);
          localStorage.setItem('token', loginRsp.data!.token);
          setLogginMessage(t("user.login-success"));
          if (loginRsp.data!.user.role === UserInfoResponseRole.TEACHER) {
            navigate(ROUTE.TEACHER.OVERVIEW);
          } else {
            navigate(ROUTE.HOME);
          }
        } else if (loginRsp.code === 80001009) {
          setLogginMessage(t("user.login-error"));
        } else if (loginRsp.code === 80007000) {
          navigate(ROUTE.ACCESS_RESTRICTED);
        } else {
          setLogginMessage(t("common.network-error"));
        }
      } else {
        setLogginMessage(t("common.network-error"));
      }
    } catch {
      setLogginMessage(t("common.network-error"));
    } finally {
      setIsLoading(false);
    }
  }

  return {
    t,
    loginMessage,
    isLoading,
    isEmailInvalid,
    isPswInvalid,
    showPassword,
    setShowPassword,
    setEmailInvalid,
    setPswInvalid,
    onLogin,
  };
}
